import { withTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import CheckboxV2 from '@/components/common/CheckboxV2';
import HoverMenu from '@/components/common/HoverMenu';
import Icon from '@/components/common/iconedLinks/Icon';
import IconedLink from '@/components/common/iconedLinks/IconedLink';
import CartLogo from '@/components/layout/header/CartLogo';
import MenuLink from '@/components/layout/navbar/MenuLink/index';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { ACCOUNT_DEFAULT_LINK, ACCOUNT_TABS, ROLES } from '@/constants/account';
import { ADMIN_TABS } from '@/constants/admin';
import { FEATURES } from '@/constants/features';
import { HEADER, Header } from '@/constants/header';
import { COLORS } from '@/constants/layoutDefaults';
import { MENU } from '@/constants/menu';
import { PAGES, PRIMEUR_DEFAULT_LINK } from '@/constants/pages';
import { COUNTRIES } from '@/constants/profile';
import { STORE_ID } from '@/constants/stores';
import { URLS } from '@/constants/urls';
import { useDisplayMyCellar, useFeature } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { setActAsCustomer } from '@/redux/slices/admin.slice';
import { disconnectUser } from '@/redux/slices/auth.slice';
import { toggleMiniCartOpenedState } from '@/redux/slices/cart.slice';
import { showConfirmModal, showLoginModal, toogleSelectLanguageModal } from '@/redux/slices/gui.slice';
import { primeurClearUser } from '@/redux/slices/primeur.slice';
import { updateUserSetting } from '@/redux/slices/user.slice';
import { UntypedObject } from '@/types';
import { getCodeISO, getCountryName } from '@/utils/deliveryCountryUtils';
import styles from './HeaderLink.module.scss';

const errorMessage = 'type does not exists in HeaderLink';
const EXCLUSIVE_CELLAR = '/exclusive-cellar.html';

type Props = {
	t: (k: string) => string;
	type: Header;
	storeConf: UntypedObject;
	loggedUser: UntypedObject;
	qtTotal: any;
	isUserConnected: boolean;
	onOpen: () => void;
	isOpen: boolean;
	onClose: () => void;
	forceClose: boolean;
	actAsCustomer: UntypedObject;
	userSettings: UntypedObject;
};

export const HeaderLink: FC<Props> = ({ t, type, isUserConnected, onOpen, isOpen, onClose, forceClose, userSettings }) => {
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
	const qtTotal = useAppSelector(({ cart }) => cart.content.qtTotal);
	const storeIdForPrice = useAppSelector(({ gui }) => gui.storeIdForPrice);
	const actAsCustomer = useAppSelector(({ admin }) => admin.actAsCustomer);
	const cartStorageQt = useAppSelector(({ storage }) => storage.cartStorageQtTotal);

	const dispatch = useAppDispatch();

	const isWishlistFeatureEnabled = useFeature(storeConf, FEATURES.MODULE_WISHLIST);
	const isCustomerLoyaltyFeatureEnabled = useFeature(storeConf, FEATURES.MODULE_CUSTOMER_LOYALTY);
	const isFeatureAddCardsEnable = useFeature(storeConf, FEATURES.MODULE_ADD_CARDS);
	const showText = !useScreenSizeContext().isMobile;
	const displayMyCellar = useDisplayMyCellar();

	const fontColor = storeConf.headerFontColor || COLORS.HEADER_FONT_COLOR;
	const whiteIcon = storeConf.headerIcons !== 'black';

	const disconnectConfirm = () => {
		dispatch(
			showConfirmModal({
				content: {
					cancel: t('forms.radio.camelFalse'),
					confirm: t('forms.radio.camelTrue'),
					content: t('header.logout.content'),
					title: t('header.logout.title'),
					confirmClick: () => dispatch(disconnectUser()),
				},
			}),
		);
	};

	const link = useMemo(() => {
		switch (type) {
			case HEADER.CONTACT:
				return (
					storeConf.codeISO === COUNTRIES.USA && {
						picto: whiteIcon ? URLS.LOCATION_WHITE : URLS.LOCATION_BLACK,
						name: t('header.location'),
						urlKeyword: '/our-store.html',
						isNofollow: true,
					}
				);
			case HEADER.QUESTION:
				return {
					picto: whiteIcon ? URLS.MAIL_WHITE : URLS.MAIL_BLACK,
					name: t('needHelp'),
					urlKeyword: '/contact.html',
					isNofollow: true,
				};
			case HEADER.PRIMEUR:
				return {
					picto: whiteIcon ? URLS.NOTIFICATION_WHITE : URLS.NOTIFICATION_BLACK,
					name: t('alert.link'),
					urlKeyword: PRIMEUR_DEFAULT_LINK,
					showText,
				};
			case HEADER.ADMIN:
				return {
					picto: whiteIcon ? URLS.ADMIN_WHITE : URLS.ADMIN_BLACK,
					urlKeyword: PAGES.ADMIN,
					isNofollow: true,
					showText: false,
				};
			case HEADER.ACCOUNT:
				return {
					picto: isUserConnected
						? whiteIcon
							? URLS.LOGIN_WHITE
							: URLS.LOGIN_BLACK
						: whiteIcon
							? URLS.ACCOUNT_WHITE
							: URLS.ACCOUNT_BLACK,
					name: t('account.myAccountFull'),
					subtext: loggedUser?.isEc ? t('account.exclusiveCellar') : null,
					urlKeyword: isUserConnected ? ACCOUNT_DEFAULT_LINK : null,
					isNofollow: true,
					showText: false,
				};
			case HEADER.CART:
				return null;
			case HEADER.LEAVE_CELLAR:
				return {
					picto: URLS.ARROW_LEFT_WHITE_OLD,
					name: t('header.leaveStorage'),
					urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.PROFILE}`,
					isNofollow: true,
					showText,
				};
			case HEADER.RETURN_TO_ACCOUNT:
				return {
					picto: URLS.ARROW_LEFT_WHITE_OLD,
					name: t('header.myAccount'),
					urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.PROFILE}`,
					isNofollow: true,
					showText,
				};
			case HEADER.RETURN_TO_EXPRO:
				return {
					picto: URLS.ARROW_LEFT_WHITE_OLD,
					name: t('btn.return'),
					urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.EXPRO}`,
					isNofollow: true,
					showText,
				};
			case HEADER.DELIVERY_COUNTRY:
				const selectedCountry = storeConf?.otherLanguagesByCountry[getCodeISO(storeConf, storeIdForPrice)];
				return {
					picto:
						selectedCountry.storeId === STORE_ID.COM
							? URLS.GLOBAL_WHITE
							: URLS.STORE_FLAG.replace('{0}', selectedCountry.storeId),
					name: getCountryName(selectedCountry, t),
					showText: false,
				};
			default:
				throw new Error(errorMessage);
		}
	}, [type, storeConf, showText, isUserConnected, loggedUser, qtTotal, cartStorageQt]);

	const ACCOUNT_MENU = [
		loggedUser?.isEc && {
			desc: 'exclusiveCellar',
			name: t('account.exclusiveCellar'),
			urlKeyword: EXCLUSIVE_CELLAR,
		},
		{
			desc: 'profile',
			name: t(`account.${ACCOUNT_TABS.PROFILE}.link`),
			urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.PROFILE}`,
			isNofollow: true,
		},
		{
			desc: 'address',
			name: t(`account.${ACCOUNT_TABS.ADDRESS}.link`),
			urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.ADDRESS}`,
			isNofollow: true,
		},
		!(storeConf.codeISO === COUNTRIES.USA) &&
			isFeatureAddCardsEnable && {
				desc: 'cards',
				name: t(`account.${ACCOUNT_TABS.CARDS}.link`),
				urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.CARDS}`,
				isNofollow: true,
			},
		{
			desc: 'orders',
			name: t(`account.${ACCOUNT_TABS.ORDERS}.link`),
			urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.ORDERS}`,
			isNofollow: true,
		},
		isWishlistFeatureEnabled && {
			desc: 'wishList',
			name: t(`account.${ACCOUNT_TABS.WISHLIST}.link`),
			urlKeyword: `${PAGES.ACCOUNT}/${ACCOUNT_TABS.WISHLIST}`,
			isNofollow: true,
		},
		displayMyCellar && {
			desc: 'cellar',
			name: t(`account.${ACCOUNT_TABS.CELLAR}.link`),
			urlKeyword: `${ACCOUNT_TABS.CELLAR}`,
			isNofollow: true,
		},
		isCustomerLoyaltyFeatureEnabled && {
			desc: 'premium',
			name: t('account.premium'),
			urlKeyword: `${ACCOUNT_TABS.CUSTOMER_LOYALTY}`,
			isNofollow: true,
		},
	];

	const ADMIN_MENU = [
		{
			desc: 'users-create',
			name: t('header.admin.users.create'),
			urlKeyword: `${PAGES.ADMIN}/${ADMIN_TABS.USERS.CREATE}`,
			isNofollow: true,
		},
		{
			desc: 'users-search',
			name: t('header.admin.users.search'),
			urlKeyword: `${PAGES.ADMIN}/${ADMIN_TABS.USERS.SEARCH}`,
			isNofollow: true,
		},
		{
			desc: 'promo-create',
			name: t('header.admin.promo.create'),
			urlKeyword: `${PAGES.ADMIN}/${ADMIN_TABS.PROMOTIONS.CREATE}`,
			isNofollow: true,
		},
		{
			desc: 'orders',
			name: t('header.admin.orders'),
			urlKeyword: `${PAGES.ADMIN}/${ADMIN_TABS.ORDERS}`,
			isNofollow: true,
		},
	];

	const handleShowAdminBadges = (e) => {
		dispatch(updateUserSetting({ name: 'showAdminBadges', value: e.target.checked }));
	};

	const handleQuitActAsMode = () => {
		dispatch(setActAsCustomer({ customer: undefined }));
		dispatch(primeurClearUser());
	};

	const logUser = () => {
		dispatch(showLoginModal());
	};

	const hoverMenu = useMemo(() => {
		switch (type) {
			case HEADER.CONTACT:
			case HEADER.QUESTION:
			case HEADER.PRIMEUR:
			case HEADER.LEAVE_CELLAR:
			case HEADER.RETURN_TO_ACCOUNT:
			case HEADER.RETURN_TO_EXPRO:
			case HEADER.DELIVERY_COUNTRY:
			case HEADER.CART:
				return null;
			case HEADER.ADMIN:
				return (
					<HoverMenu shouldOpen={isOpen} forceClose={forceClose} closeWhenClickOutside>
						<div className={styles['hover-menu']} data-test-id="admin-hover-menu" data-rbf="admin-hover-menu">
							<div className={styles['hover-menu-title']}>{t('header.admin.title')}</div>
							{ADMIN_MENU.map((item, i) => (
								<div className={styles['hover-menu-item']} key={i}>
									<MenuLink id={`admin-${item.desc}`} isMobile={false} link={item} type={MENU.LIST} />
								</div>
							))}
							{!loggedUser.roles?.includes(ROLES.HIDE_MARGINS) && (
								<div className={styles['hover-menu-checkbox-container']}>
									<CheckboxV2
										id="admin-show-stock-margin"
										label={t('header.admin.showAdminBadges')}
										checked={!!userSettings.showAdminBadges}
										onChange={handleShowAdminBadges}
										className={styles.checkbox}
									/>
								</div>
							)}
							{actAsCustomer && (
								<div
									id="ti-admin-hover-menu-extra"
									className={styles['admin-hover-menu-extra']}
									data-test-id="admin-hover-menu-extra"
								>
									<div className={styles['admin-hover-menu-extra-title']}>
										<Icon src={URLS.ICON_INFO_HELP} alt="help" />
										&nbsp;
										{t('header.admin.actAs.mode')}
									</div>
									<div
										className={styles['admin-hover-menu-extra-info']}
										data-test-id="admin-hover-menu-extra-info"
									>
										{t('header.admin.actAs.informativeMessage')
											.replace('{firstname}', actAsCustomer.firstName)
											.replace('{lastname}', actAsCustomer.lastName)}
									</div>
									<div className={styles['admin-hover-menu-extra-link']}>
										<IconedLink
											rbf="quit-act-as-mode"
											icon={URLS.LEAVE_ACT_AS}
											text={t('header.admin.actAs.quitMode')}
											onClicked={() => handleQuitActAsMode()}
										/>
									</div>
								</div>
							)}
						</div>
					</HoverMenu>
				);
			case HEADER.ACCOUNT:
				return (
					<HoverMenu
						shouldOpen={isOpen}
						forceClose={forceClose}
						closeWhenClickOutside
						data-test-id="account-hover-menu"
						data-rbf="account-hover-menu"
					>
						<div
							className={`${styles['account-hover-menu']} ${styles['hover-menu']}`}
							data-test-id="hover-menu"
							data-rbf="hover-menu"
						>
							<div className={styles['hover-menu-title']}>{t('account.myAccountFull')}</div>
							{ACCOUNT_MENU.filter((elem) => !!elem).map((item, i) => (
								<div className={styles['hover-menu-item']} key={i}>
									<MenuLink id={`account-${item.desc}`} isMobile={false} link={item} type={MENU.LIST} />
								</div>
							))}
							<div
								data-rbf="account-hover-menu-logout"
								className={styles['account-hover-menu-logout']}
								onClick={disconnectConfirm}
							>
								<span>{t('header.logout.title')}</span>
								<Icon src={URLS.DECONNEXION} alt={t('header.logout.title')} width={12} height={12} />
							</div>
						</div>
					</HoverMenu>
				);
			default:
				throw new Error(errorMessage);
		}
	}, [type, isOpen, forceClose, actAsCustomer, userSettings]);

	const linkComponent = useMemo(() => {
		switch (type) {
			case HEADER.CONTACT:
			case HEADER.QUESTION:
			case HEADER.PRIMEUR:
			case HEADER.LEAVE_CELLAR:
			case HEADER.RETURN_TO_ACCOUNT:
			case HEADER.RETURN_TO_EXPRO:
				return <MenuLink id={type} isMobile={false} link={link} type={HEADER.ICONED_LINK} fontColor={fontColor} />;
			case HEADER.ACCOUNT:
				return (
					<MenuLink
						id={type}
						isMobile={false}
						link={link}
						type={HEADER.ICONED_LINK}
						handleMouseEnter={onOpen}
						handleMouseLeave={onClose}
						handleClick={isUserConnected ? null : logUser}
						fontColor={fontColor}
					>
						{hoverMenu}
					</MenuLink>
				);
			case HEADER.ADMIN:
				return (
					<MenuLink
						id={type}
						isMobile={false}
						link={link}
						type={HEADER.ICONED_LINK}
						handleMouseEnter={onOpen}
						handleMouseLeave={onClose}
						fontColor={fontColor}
					>
						{hoverMenu}
					</MenuLink>
				);
			case HEADER.CART:
				return (
					<div onClick={() => dispatch(toggleMiniCartOpenedState())} style={{ cursor: 'pointer' }}>
						<CartLogo total={qtTotal + cartStorageQt || 0} whiteIcon={whiteIcon} />
					</div>
				);
			case HEADER.DELIVERY_COUNTRY:
				return (
					<MenuLink
						id={type}
						isMobile={false}
						link={link}
						type={HEADER.ICONED_LINK}
						fontColor={fontColor}
						handleClick={() => dispatch(toogleSelectLanguageModal({ opened: true }))}
					/>
				);
			default:
				throw new Error(errorMessage);
		}
	}, [type, link, isUserConnected, hoverMenu, onOpen, onClose, qtTotal, cartStorageQt]);

	return (
		<div className={styles['header-link']} data-test-id="header-link">
			{linkComponent}
		</div>
	);
};

export default withTranslation(NS.COMMON)(HeaderLink);
